import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Optional,
  Output,
  Self,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
} from '@angular/forms';
import {Icon} from '@tisa-group/client-ui/components/icon';

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {'(blur)': 'onTouched()'},
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputPrimitiveComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class InputPrimitiveComponent<T> implements ControlValueAccessor {
  protected controlValue: T | undefined;

  onChange!: (value: T | undefined) => void;

  onTouched!: () => void;

  @HostBinding(`attr.aria-required`)
  get requiredAttribute(): boolean {
    return this.required;
  }

  @HostBinding('attr.disabled')
  @Input()
  disabled = false;

  /** TODO: Remove and consider required state based on control validators*/
  @Input()
  required = false;

  @Input()
  placeholder: string | undefined;

  @Input()
  icon: Icon | undefined;

  @Output()
  onBlur = new EventEmitter<void>();

  @Output()
  onFocus = new EventEmitter<void>();

  get invalid(): boolean {
    return !!(this.control && this.control.invalid);
  }

  get showErrors(): boolean {
    return this.invalid && !!this.control.touched;
  }

  get errors(): ValidationErrors | null {
    return this.control ? this.control.errors : null;
  }

  get value(): T | undefined {
    return this.controlValue;
  }

  @Input()
  set value(value: T | undefined) {
    this.controlValue = value;
    this.notifyValueChange();
  }

  constructor(
    readonly changeDetectorRef: ChangeDetectorRef,
    @Self() @Optional() protected control: NgControl,
  ) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  onInputBlur() {
    this.onBlur.next();
    this.onTouched();
  }

  writeValue(value: T): void {
    this.controlValue = value;
    this.changeDetectorRef.markForCheck();
  }

  notifyValueChange(): void {
    if (this.onChange) {
      this.onChange(this.controlValue);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
